import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const SESSION_KEY = "concierge_session_v1";

// Suggested starter prompts — give new users a frictionless first interaction.
const STARTERS = [
  "I'm shopping for pet food on a budget",
  "Any good restaurant codes this week?",
  "Running shoes under $80",
  "Help me save on groceries",
];

/**
 * ConciergeChat — floating "Ask the Clipper" chat bubble (bottom-right).
 *
 * Streams Claude Sonnet responses via SSE from /api/ai/concierge. Session id
 * persists across refreshes so multi-turn chat continues naturally — Claude
 * remembers earlier picks in the same conversation.
 */
export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => {
    try {
      let s = localStorage.getItem(SESSION_KEY);
      if (!s) {
        s = "c_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        localStorage.setItem(SESSION_KEY, s);
      }
      return s;
    } catch {
      return "c_fallback_" + Date.now();
    }
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    // Create a placeholder for the streaming response
    setMessages((m) => [...m, { role: "assistant", text: "" }]);

    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    try {
      const res = await fetch(`${base}/api/ai/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events arrive as "data: {...}\n\n"
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const chunk of lines) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          try {
            const ev = JSON.parse(payload);
            if (ev.delta) {
              setMessages((m) => {
                if (m.length === 0) return m;
                const last = m[m.length - 1];
                if (last.role !== "assistant") return m;
                const updated = { ...last, text: (last.text || "") + ev.delta };
                return [...m.slice(0, -1), updated];
              });
            } else if (ev.error) {
              setMessages((m) => {
                if (m.length === 0) return m;
                const last = m[m.length - 1];
                if (last.role !== "assistant") return m;
                return [...m.slice(0, -1), { ...last, text: "Sorry — I hit a snag. Try again?" }];
              });
            }
          } catch {
            /* ignore malformed SSE frame */
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        if (m.length === 0) return m;
        const last = m[m.length - 1];
        if (last.role !== "assistant" || last.text) return m;
        return [...m.slice(0, -1), { ...last, text: "The concierge is briefly offline — give it a moment and try again." }];
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="concierge-open"
        className="fixed bottom-5 right-5 z-50 h-14 pr-5 pl-4 rounded-full bg-[#2d2a32] text-white border-2 border-[#2d2a32] shadow-[4px_4px_0_#ff6b6b] hover:shadow-[6px_6px_0_#ff6b6b] hover:-translate-y-0.5 transition-all flex items-center gap-2 group"
      >
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#f7c873] group-hover:bg-[#ff6b6b] transition-colors">
          <Sparkles className="w-4 h-4 text-[#2d2a32]" strokeWidth={2.5} />
        </span>
        <span className="serif text-base leading-none pt-0.5">Ask the Clipper</span>
      </button>
    );
  }

  return (
    <div
      data-testid="concierge-panel"
      className="fixed bottom-5 right-5 z-50 w-[min(380px,calc(100vw-24px))] h-[min(540px,calc(100vh-120px))] bg-[#faf5e6] border-2 border-[#2d2a32] rounded-2xl shadow-[6px_6px_0_#2d2a32] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#2d2a32] bg-[#2d2a32] text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#f7c873]" />
          <div>
            <div className="serif text-lg leading-none">The Clipper</div>
            <div className="text-[10px] tracking-[0.2em] opacity-70">AI SHOPPING CONCIERGE</div>
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="concierge-close"
          className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 text-sm leading-relaxed">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-[#3a3a38] leading-relaxed">
              Tell me what you&apos;re shopping for and I&apos;ll find the best verified codes we have right now.
            </p>
            <div className="flex flex-col gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid={`concierge-starter-${s.slice(0, 10)}`}
                  className="text-left text-xs border-2 border-dashed border-[#2d2a32]/40 hover:border-[#2d2a32] bg-white rounded-md px-3 py-2 transition-colors"
                >
                  💬 {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`concierge-msg-${m.role}`}
            className={m.role === "user" ? "text-right" : "text-left"}
          >
            <div
              className={
                m.role === "user"
                  ? "inline-block max-w-[85%] bg-[#2d2a32] text-white px-3 py-2 rounded-xl rounded-br-sm"
                  : "inline-block max-w-[90%] bg-white border-2 border-[#2d2a32] px-3 py-2 rounded-xl rounded-bl-sm whitespace-pre-wrap"
              }
            >
              {m.text || <Loader2 className="w-3.5 h-3.5 animate-spin inline" />}
            </div>
          </div>
        ))}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t-2 border-[#2d2a32] p-2 flex items-center gap-2 bg-white"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="What are you shopping for?"
          disabled={busy}
          data-testid="concierge-input"
          className="flex-1 border-2 border-[#2d2a32] bg-[#faf5e6] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#ff6b6b] disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="concierge-send"
          className="w-10 h-10 rounded-full bg-[#ff6b6b] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#2d2a32] transition-colors"
          aria-label="Send"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
